import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button, IconButton } from "@dfws/ui";
import { useDeleteFilter, useFilters, useSaveFilter, type Filter } from "../../lib/queries";
import { describeActions, describeCriteria, isBlockRule } from "./filterText";
import { FilterEditor } from "./FilterEditor";
import { QuickRules } from "./QuickRules";
import { Notice } from "./parts";
import s from "./Settings.module.css";

export function FiltersSection() {
  const filters = useFilters();
  const del = useDeleteFilter();
  const save = useSaveFilter();
  const [editing, setEditing] = useState<Partial<Filter> | null | undefined>(undefined);
  // Prefill from "Filter messages like this" (query string ?from=...)
  const prefill = typeof location !== "undefined" ? new URLSearchParams(location.search).get("from") : null;
  const [opened, setOpened] = useState(false);
  if (prefill && !opened && editing === undefined) {
    setOpened(true);
    setEditing({ criteria: { from: prefill }, actions: {} });
  }
  const items = (filters.data?.items ?? []).filter((f) => !isBlockRule(f.criteria, f.actions) && !f.criteria.kind);

  return (
    <>
      <p className={s.lead}>Sort, star, archive or forward mail automatically as it arrives. Filters run on the DevFusion mail server, so they also work when Workspace is closed and in other mail apps.</p>
      {filters.data?.syncError && <Notice kind="warn">{filters.data.syncError}</Notice>}
      <QuickRules filters={filters.data?.items ?? []} loading={filters.isPending} />
      <h3 className={s.groupTitle} style={{ marginBottom: "var(--space-3)" }}>Your filters</h3>
      <div className={s.actionsBar} style={{ marginTop: 0, marginBottom: "var(--space-4)" }}>
        <Button variant="primary" icon={<Plus />} onClick={() => setEditing(null)}>Create a filter</Button>
      </div>
      {filters.isPending ? (
        <p className={s.muted}>Loading filters...</p>
      ) : items.length === 0 ? (
        <div className={s.empty}>No filters yet. Open a message and choose Filter messages like this, or create one here.</div>
      ) : (
        <ul className={s.list}>
          {items.map((f) => (
            <li key={f.id} className={s.listItem}>
              <input
                type="checkbox"
                role="switch"
                className={s.switch}
                checked={f.enabled}
                aria-label={f.enabled ? "Filter on" : "Filter off"}
                onChange={(e) => save.mutate({ ...f, enabled: e.target.checked })}
              />
              <div className={s.listMain}>
                <div className={s.listTitle} dir="auto">{describeCriteria(f.criteria).map((x) => <span key={x} className={s.chip}>{x}</span>)}</div>
                <div className={s.listSub}>{describeActions(f.actions).join(", ")}</div>
              </div>
              <IconButton label="Edit filter" icon={<Pencil />} size="sm" onClick={() => setEditing(f)} />
              <IconButton label="Delete filter" icon={<Trash2 />} size="sm" onClick={() => del.mutate(f.id)} />
            </li>
          ))}
        </ul>
      )}
      {editing !== undefined && <FilterEditor initial={editing} onClose={() => setEditing(undefined)} />}
    </>
  );
}
